/**
 * The selected link of a page (1.J deliverable 14): in memory only, one per document, read by the
 * Inspector. Focusing or clicking a Story panel row, a relation-list row or a Backlinks relation
 * selects its link; a removed or vanished link clears the selection.
 */
import { useSyncExternalStore } from 'react';

const selected = new Map<string, string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Selects `linkId` on `docId`; `null` clears. Nothing is notified when nothing changes. */
export function selectLink(docId: string, linkId: string | null): void {
  if ((selected.get(docId) ?? null) === linkId) return;
  if (linkId === null) selected.delete(docId);
  else selected.set(docId, linkId);
  emit();
}

export function selectedLink(docId: string): string | null {
  return selected.get(docId) ?? null;
}

export function useSelectedLink(docId: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => selected.get(docId) ?? null,
    () => null,
  );
}

/** The selection once `liveIds` are the links that exist: a vanished link is no selection. */
export function reconcileSelection(selection: string | null, liveIds: ReadonlySet<string>) {
  return selection !== null && liveIds.has(selection) ? selection : null;
}

/** Clears the selection of `docId` when its link is not among `liveIds`. */
export function pruneSelection(docId: string, liveIds: ReadonlySet<string>): void {
  const current = selectedLink(docId);
  if (current !== null && reconcileSelection(current, liveIds) === null) selectLink(docId, null);
}
